import { z } from "zod";
import {
  ERPSyncConfigSchema,
  OpportunityCustomFieldMappingSchema,
} from "./schemas";

/**
 * Per-customer Eniteo sync configuration. Stored as `integrationConfig` jsonb on
 * the `erpIntegrationSyncsTable` row. Keeps every pull toggle and the quote push
 * toggle from the shared {@link ERPSyncConfigSchema}, which the sync form disables
 * until the matching capability is reported. Drops the company, person and
 * opportunity push toggles, since Eniteo customers are created in Eniteo.
 */
export const EniteoSyncConfigSchema = ERPSyncConfigSchema.omit({
  pushCompanies: true,
  pushPeople: true,
  pushOpportunities: true,
}).extend({
  overwriteQuoteOnRevision: z.boolean().default(false).meta({
    title: "Overwrite the Eniteo quote on a new revision",
    description:
      "Rewrite the lines and comments of the existing Eniteo quote when a new Forgepoint revision is finalized, instead of creating a second Eniteo quote.",
  }),
  itemCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Item custom field mapping",
    description: "Map Forgepoint item custom fields to Eniteo item fields",
  }),
  companyCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Company custom field mapping",
    description:
      "Map Forgepoint company custom fields to Eniteo customer fields, for example a Payment Terms field to Terms.",
  }),
  quoteCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Quote custom field mapping",
    description:
      "Fill Forgepoint quote custom fields after a push, for example with the Eniteo quote number, or send a quote custom field on the push, for example as the Eniteo Salesperson 1.",
  }),
});

/** Mapping target that fills a text custom field with the Eniteo quote number after a push. */
export const ENITEO_QUOTE_NUMBER_TARGET = "QuoteNum";

/** Mapping target that sends a text quote custom field to Eniteo as the quote's Salesperson 1, matched by salesperson name. */
export const ENITEO_QUOTE_SALESPERSON_TARGET = "Salesperson1";

/** Per-customer Eniteo sync configuration. */
export type EniteoSyncConfig = z.infer<typeof EniteoSyncConfigSchema>;

/** Display order for the Eniteo sync config properties. */
export const ENITEO_SYNC_CONFIG_PROPERTY_ORDER: string[] = [
  "syncCompanies",
  "syncPeople",
  "syncOpportunities",
  "syncPriceLists",
  "syncItems",
  "syncOperations",
  "syncLocations",
  "syncInventoryLines",
  "pushQuotes",
  "overwriteQuoteOnRevision",
  "itemCustomFieldMapping",
  "companyCustomFieldMapping",
  "quoteCustomFieldMapping",
];
